/**
 * Field-edit configuration for the agent HITL confirmation modal.
 *
 * Maps each proposed write-tool field to how it should be edited inline: free
 * text, a single select, a multi-select (with its option set), or read-only.
 * Option sources are reused from the existing incident/ICS forms — this file
 * does NOT define new option lists except for the two status enums that live
 * only in the backend tool schemas.
 *
 * It also knows where each tool nests its fields in the tool args (so an edit
 * lands in the right place): the generic create_ics_record / update_ics_record
 * tools nest entity fields under `data`; update_incident_info keeps them
 * top-level. `edited_args` fully replaces the tool args on resume, so
 * buildEditedArgs() merges edits onto a copy of the original args.
 */

import {
  USCGResourceKindOptions,
  TypeOptions,
} from 'components/IncidentConfig/ResourceTypes';
import { incidentObjectiveTypeSegments } from 'components/IAP/incidentObjectiveTypes';
import {
  PHYSICAL_HAZARDS,
  ENVIRONMENTAL_HAZARDS,
  OTHER_HAZARDS,
  POTENTIAL_HAZARDS,
  REQUIRED_PPE,
  HAZMAT_CLASSIFICATION,
  INCIDENT_NEEDS,
} from 'components/IAP/initialResponsePage/SafetyAnalysis/types';

export type EditKind =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiselect'
  /** Plain object edited as labeled per-key rows (e.g. weather). */
  | 'object'
  | 'readonly';

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConfig {
  editKind: EditKind;
  /**
   * Human-readable label shown in the confirmation UI (falls back to a
   * humanized field key).
   */
  label?: string;
  /** Static option set for select / multiselect. */
  options?: SelectOption[];
  /**
   * Marks a field whose options must be resolved at render time from a hook
   * (id→name lists keyed here): AORs, incident objectives, resources, or roster
   * members (POC). ReviewQueueModal supplies the resolved option sets.
   */
  dynamicOptions?: 'aor' | 'objective' | 'resource' | 'poc';
}

const stringsToOptions = (values: readonly string[]): SelectOption[] =>
  values.map((v) => ({ value: v, label: v }));

// Option sets reused from existing forms.
const OBJECTIVE_TYPE_OPTIONS: SelectOption[] =
  incidentObjectiveTypeSegments.map((s) => ({
    value: s.value,
    label: s.tooltip,
  }));

// Status enums live only in the backend write-tool schemas (entityDescriptors.ts); mirror them here so an edit
// validates server-side. Keep in sync with COMS ACTION_STATUSES / RESOURCE_STATUSES.
const ACTION_STATUS_OPTIONS = stringsToOptions([
  'Planned',
  'Current/Ongoing',
  'Closed',
]);
const RESOURCE_STATUS_OPTIONS = stringsToOptions([
  'Onsite',
  'Ordered',
  'In Transit',
  'Deployed',
]);

/** Tools whose entity fields are nested under args.data (vs. top-level). */
const DATA_NESTED_TOOLS = new Set(['create_ics_record', 'update_ics_record']);

export const isDataNestedTool = (toolName: string): boolean =>
  DATA_NESTED_TOOLS.has(toolName);

/**
 * Multi-field single-record proposals reviewed one field at a time (each diff
 * field becomes its own card in the review queue). Record-per-call entities
 * (objectives, actions, resources, roster positions) already arrive as one
 * confirmation per record and stay whole.
 */
const SPLIT_ENTITIES = new Set(['safety_message', 'hazmat']);

export const isSplitConfirmation = (
  toolName: string,
  entity: string | undefined
): boolean =>
  toolName === 'update_incident_info' ||
  (isDataNestedTool(toolName) && !!entity && SPLIT_ENTITIES.has(entity));

/**
 * Fields the backend Zod schema requires on create — rejecting one of these
 * cards rejects the WHOLE create (the remaining fields would fail validation on
 * resume without it), rather than dropping just that field.
 */
const REQUIRED_ON_CREATE: Record<string, Set<string>> = {
  safety_message: new Set(['safetyMessage']),
};

export const isRequiredOnCreate = (
  toolName: string,
  entity: string | undefined,
  field: string
): boolean =>
  toolName === 'create_ics_record' &&
  !!entity &&
  (REQUIRED_ON_CREATE[entity]?.has(field) ?? false);

/**
 * Per-entity field configs for the generic ICS record tools, keyed by entity
 * then field.
 */
const ICS_RECORD_FIELDS: Record<string, Record<string, FieldConfig>> = {
  objective: {
    name: { editKind: 'text', label: 'Name' },
    description: { editKind: 'textarea', label: 'Description' },
    type: {
      editKind: 'select',
      label: 'Type',
      options: OBJECTIVE_TYPE_OPTIONS,
    },
  },
  action: {
    name: { editKind: 'text', label: 'Name' },
    description: { editKind: 'textarea', label: 'Description' },
    status: {
      editKind: 'select',
      label: 'Status',
      options: ACTION_STATUS_OPTIONS,
    },
    // Show the linked objective / resources / POC by NAME (options resolved at
    // render time); the select still stores the underlying id for resume.
    objective_id: {
      editKind: 'select',
      label: 'Objective',
      dynamicOptions: 'objective',
    },
    resources: {
      editKind: 'multiselect',
      label: 'Resources',
      dynamicOptions: 'resource',
    },
    poc: {
      editKind: 'select',
      label: 'Point of Contact',
      dynamicOptions: 'poc',
    },
    start_date: { editKind: 'text', label: 'Start Date' },
    target_date: { editKind: 'text', label: 'Target Date' },
  },
  resource: {
    name: { editKind: 'text', label: 'Name' },
    kind: {
      editKind: 'select',
      label: 'Kind',
      options: USCGResourceKindOptions,
    },
    type: { editKind: 'select', label: 'Type', options: TypeOptions },
    quantity: { editKind: 'text', label: 'Quantity' },
    status: {
      editKind: 'select',
      label: 'Status',
      options: RESOURCE_STATUS_OPTIONS,
    },
    description: { editKind: 'textarea', label: 'Description' },
    eta: { editKind: 'text', label: 'ETA' },
    date_time_ordered: { editKind: 'text', label: 'Date/Time Ordered' },
  },
  // Roster (ICS 201 §6). position_key is constrained server-side to the canonical ICS position set; kept as a labeled
  // text field here so DICE doesn't duplicate/drift that list.
  org_assignment: {
    position_key: { editKind: 'text', label: 'Position' },
    name: { editKind: 'text', label: 'Assigned To' },
  },
  // Safety message (ICS 201 §8).
  safety_message: {
    safetyMessage: { editKind: 'textarea', label: 'Safety Message' },
    safetyOfficer: { editKind: 'text', label: 'Safety Officer' },
    dutyPhone: { editKind: 'text', label: 'Duty Phone' },
    email: { editKind: 'text', label: 'Email' },
    sopAndSafeWorkPractices: {
      editKind: 'textarea',
      label: 'SOP & Safe Work Practices',
    },
    comments: { editKind: 'textarea', label: 'Comments' },
    datetimeInitiated: { editKind: 'text', label: 'Initiated' },
  },
  // HAZMAT (ICS 201 §8 / ICS 208HM). material[] is a complex array — left to the readonly fallback.
  hazmat: {
    classifications: {
      editKind: 'multiselect',
      label: 'Classifications',
      options: stringsToOptions(HAZMAT_CLASSIFICATION),
    },
    incidentNeeds: {
      editKind: 'multiselect',
      label: 'Incident Needs',
      options: stringsToOptions(INCIDENT_NEEDS),
    },
    otherClassification: { editKind: 'text', label: 'Other Classification' },
    sops: { editKind: 'textarea', label: 'SOPs' },
    decontaminationProcedure: {
      editKind: 'textarea',
      label: 'Decontamination Procedure',
    },
    emergencyProcedures: {
      editKind: 'textarea',
      label: 'Emergency Procedures',
    },
    additionalProductInfo: {
      editKind: 'textarea',
      label: 'Additional Product Info',
    },
    hazardMonitoringComments: {
      editKind: 'textarea',
      label: 'Hazard Monitoring Comments',
    },
  },
};

// Keys are camelCase to match the backend update_incident_info tool input
// (UpdateIncidentInfoTool schema), NOT the snake_case DB columns.
const UPDATE_INCIDENT_INFO_FIELDS: Record<string, FieldConfig> = {
  situationSummary: { editKind: 'textarea', label: 'Situation Summary' },
  priorities: { editKind: 'textarea', label: 'Priorities' },
  operationalPeriodCommandEmphasis: {
    editKind: 'textarea',
    label: 'Command Emphasis',
  },
  incidentLocation: { editKind: 'text', label: 'Incident Location' },
  keyDecisionsAndProcedures: {
    editKind: 'textarea',
    label: 'Key Decisions & Procedures',
  },
  // ICS 201 §8 Safety Analysis fields (text[] columns; the agent sets these via update_incident_info).
  physicalHazards: {
    editKind: 'multiselect',
    label: 'Physical Hazards',
    options: stringsToOptions(PHYSICAL_HAZARDS),
  },
  environmentalHazards: {
    editKind: 'multiselect',
    label: 'Environmental Hazards',
    options: stringsToOptions(ENVIRONMENTAL_HAZARDS),
  },
  otherHazards: {
    editKind: 'multiselect',
    label: 'Other Hazards',
    options: stringsToOptions(OTHER_HAZARDS),
  },
  potentialHazards: {
    editKind: 'multiselect',
    label: 'Potential Hazards',
    options: stringsToOptions(POTENTIAL_HAZARDS),
  },
  requiredPpe: {
    editKind: 'multiselect',
    label: 'Required PPE',
    options: stringsToOptions(REQUIRED_PPE),
  },
  notesOnPpe: { editKind: 'textarea', label: 'Notes on PPE' },
  // weather is a structured object (wind/tides/forecast/…) — edited per key.
  weather: { editKind: 'object', label: 'Weather' },
};

const isPrimitive = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean';

export const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Resolve how a given tool/entity/field should be edited. Fields not in the
 * registry fall back by value shape: primitives → free text, plain objects →
 * labeled per-key rows, anything else (arrays of objects, etc.) → read-only —
 * so unknown complex structured data is never silently editable.
 */
export function getFieldConfig(
  toolName: string,
  entity: string | undefined,
  field: string,
  value: unknown
): FieldConfig {
  let explicit: FieldConfig | undefined;
  if (isDataNestedTool(toolName) && entity) {
    explicit = ICS_RECORD_FIELDS[entity]?.[field];
  } else if (toolName === 'update_incident_info') {
    explicit = UPDATE_INCIDENT_INFO_FIELDS[field];
  }
  if (explicit) return explicit;
  if (isPrimitive(value)) return { editKind: 'text' };
  if (isPlainObject(value)) return { editKind: 'object' };
  return { editKind: 'readonly' };
}

/** Short tokens that read as acronyms rather than words when humanizing keys. */
const ACRONYMS = new Set([
  'id',
  'eta',
  'ppe',
  'sop',
  'url',
  'gps',
  'ics',
  'iap',
  'neo',
  'gpt',
]);

/**
 * Humanize a raw field key into a Title Case label. Handles both camelCase
 * (`situationSummary`) and snake_case (`situation_summary`) → "Situation
 * Summary". Used as the fallback when a field has no configured `label`.
 */
export function humanizeFieldName(field: string): string {
  const words = field
    // split snake_case / kebab-case
    .replace(/[_-]+/g, ' ')
    // split camelCase / PascalCase boundaries (incl. ACRONYMFollowedByWord)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
}

/**
 * Resolve the human-readable label for a tool/entity/field. Prefers the
 * configured `label`; otherwise humanizes the raw key so the confirmation UI
 * never shows a snake_case / camelCase database name.
 */
export function getFieldLabel(
  toolName: string,
  entity: string | undefined,
  field: string
): string {
  let explicit: FieldConfig | undefined;
  if (isDataNestedTool(toolName) && entity) {
    explicit = ICS_RECORD_FIELDS[entity]?.[field];
  } else if (toolName === 'update_incident_info') {
    explicit = UPDATE_INCIDENT_INFO_FIELDS[field];
  }
  return explicit?.label ?? humanizeFieldName(field);
}

/** Read a field's value from the tool args, honoring the tool's nesting. */
export function getArgValue(
  args: Record<string, unknown>,
  toolName: string,
  field: string
): unknown {
  if (isDataNestedTool(toolName)) {
    const data = (args?.data as Record<string, unknown>) ?? {};
    return data[field];
  }
  return args?.[field];
}

/**
 * Build the `edited_args` payload for a resume 'edit' decision: a copy of the
 * original args with the user's field edits merged in at the correct path.
 */
export function buildEditedArgs(
  args: Record<string, unknown>,
  toolName: string,
  edits: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(args ?? {}) };
  if (isDataNestedTool(toolName)) {
    next.data = {
      ...((args?.data as Record<string, unknown>) ?? {}),
      ...edits,
    };
  } else {
    Object.assign(next, edits);
  }
  return next;
}

/**
 * Build `edited_args` for a split confirmation with mixed per-field verdicts:
 * rejected fields are removed from the copied args (update tools only write the
 * fields present, so an omitted field is simply left untouched), then the
 * accepted fields' edits are merged in. Envelope keys (entity, record ids) live
 * outside the field set and are never dropped.
 */
export function buildEditedArgsPartial(
  args: Record<string, unknown>,
  toolName: string,
  edits: Record<string, unknown>,
  omitFields: string[]
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(args ?? {}) };
  if (isDataNestedTool(toolName)) {
    const data = { ...((args?.data as Record<string, unknown>) ?? {}) };
    for (const field of omitFields) delete data[field];
    next.data = { ...data, ...edits };
  } else {
    for (const field of omitFields) delete next[field];
    Object.assign(next, edits);
  }
  return next;
}
